import { useInfiniteQuery } from "@tanstack/react-query"
import type { ProductVisualKind } from "@nova-ai/product-visual-templates/kinds"
import {
  routes,
  type ProductVisualWorklistResponse,
  type ProductVisualStudioStatus,
} from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

const PRODUCT_VISUAL_WORKLIST_PAGE_SIZE = 24

export type ProductVisualWorklistFilter = {
  store: string
  kind: ProductVisualKind
  status: Exclude<ProductVisualStudioStatus, "images">
}

export function useProductVisualWorklist(filter: ProductVisualWorklistFilter, enabled = true) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.productVisualWorklist(filter),
    enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam, signal }) => {
      const result = await apiFetch<ProductVisualWorklistResponse>(
        routes.productVisuals.worklist(),
        {
          signal,
          query: {
            store: filter.store,
            kind: filter.kind,
            status: filter.status,
            page: String(pageParam),
            pageSize: String(PRODUCT_VISUAL_WORKLIST_PAGE_SIZE),
          },
        }
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    getNextPageParam: (lastPage) => {
      const total = lastPage.counts[filter.status]
      return lastPage.page * lastPage.pageSize < total ? lastPage.page + 1 : undefined
    },
  })
  return {
    ...query,
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
    counts: query.data?.pages[0]?.counts,
  }
}
